import axios from 'axios';
// Utiliser une URL absolue pour développement
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Laisser le navigateur définir le Content-Type avec boundary pour FormData
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Ne pas rediriger vers /login si c'est une tentative de connexion échouée
    const isLoginRequest = error.config?.url?.includes('/auth/login');
    
    // Gérer les erreurs blob : convertir le blob en texte si c'est une erreur
    if (error.config?.responseType === 'blob' && error.response?.data instanceof Blob) {
      try {
        const text = await error.response.data.text();
        try {
          // Essayer de parser comme JSON
          const json = JSON.parse(text);
          error.response.data = json;
        } catch {
          // Si ce n'est pas du JSON, créer un objet avec le message
          error.response.data = {
            message: text || 'Erreur lors de la génération du document',
            error: text || 'Erreur inconnue'
          };
        }
      } catch (blobError) {
        console.error('Erreur lors de la conversion du blob d\'erreur:', blobError);
        // Créer un objet d'erreur par défaut
        error.response = error.response || {};
        error.response.data = {
          message: 'Erreur lors de la lecture de la réponse du serveur',
          error: 'Impossible de convertir la réponse d\'erreur'
        };
      }
    }
    
    // Gérer les erreurs sans réponse (réseau, timeout, etc.)
    if (!error.response && error.config?.responseType === 'blob') {
      error.response = {
        status: 0,
        data: {
          message: error.message || 'Erreur de connexion',
          error: 'Impossible de contacter le serveur'
        }
      };
    }
    
    if (error.response?.status === 401 && !isLoginRequest) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth services
export const authService = {
  login: (credentials) => api.post('/auth/login', credentials),
  register: (userData) => api.post('/auth/register', userData),
  logout: () => {
    localStorage.removeItem('token');
    window.location.href = '/login';
  },
  me: () => api.get('/auth/me'),
  changePassword: (data) => api.put('/auth/change-password', data),
};

// Eleves services
export const elevesService = {
  getAll: (params) => api.get('/eleves', { params }),
  getById: (id) => api.get(`/eleves/${id}`),
  create: (data) => api.post('/eleves', data),
  update: (id, data) => api.put(`/eleves/${id}`, data),
  delete: (id) => api.delete(`/eleves/${id}`),
};

// Classes services
export const classesService = {
  getAll: (params) => api.get('/classes', { params }),
  getById: (id) => api.get(`/classes/${id}`),
  create: (data) => api.post('/classes', data),
  update: (id, data) => api.put(`/classes/${id}`, data),
  delete: (id) => api.delete(`/classes/${id}`),
};

// Inscriptions services
export const inscriptionsService = {
  getAll: (params) => api.get('/inscriptions', { params }),
  getById: (id) => api.get(`/inscriptions/${id}`),
  create: (data) => api.post('/inscriptions', data),
  update: (id, data) => api.put(`/inscriptions/${id}`, data),
  delete: (id) => api.delete(`/inscriptions/${id}`),
};

// Paiements services
export const paiementsService = {
  getAll: (params) => api.get('/paiements', { params }),
  getById: (id) => api.get(`/paiements/${id}`),
  create: (data) => api.post('/paiements', data),
  update: (id, data) => api.put(`/paiements/${id}`, data),
};

// Frais de scolarité services
export const feeCategoriesService = {
  getAll: (params) => api.get('/fee-categories', { params }),
  getById: (id) => api.get(`/fee-categories/${id}`),
  create: (data) => api.post('/fee-categories', data),
  update: (id, data) => api.put(`/fee-categories/${id}`, data),
  delete: (id) => api.delete(`/fee-categories/${id}`),
};

export const feeStructuresService = {
  getAll: (params) => api.get('/fee-structures', { params }),
  getById: (id) => api.get(`/fee-structures/${id}`),
  create: (data) => api.post('/fee-structures', data),
  update: (id, data) => api.put(`/fee-structures/${id}`, data),
  delete: (id) => api.delete(`/fee-structures/${id}`),
  getStudentInstallments: (inscriptionId) =>
    api.get(`/fee-structures/inscription/${inscriptionId}/installments`),
  generateInstallments: (inscriptionId) =>
    api.post(`/fee-structures/inscription/${inscriptionId}/generate`),
};

export const feePaymentsService = {
  getAll: (params) => api.get('/fee-payments', { params }),
  getById: (id) => api.get(`/fee-payments/${id}`),
  create: (data) => api.post('/fee-payments', data),
  cancel: (id, data) => api.post(`/fee-payments/${id}/cancel`, data),
  getTypeReglements: () => api.get('/fee-payments/type-reglements'),
};

export const feeStatsService = {
  getDashboard: (params) => api.get('/fee-stats/dashboard', { params }),
  getOverdue: (params) => api.get('/fee-stats/overdue', { params }),
  getByClass: (params) => api.get('/fee-stats/by-class', { params }),
};

export const feeReminderService = {
  preview: (inscriptionId) => api.get(`/fee-reminders/whatsapp/preview/${inscriptionId}`),
  send: (data) => api.post('/fee-reminders/whatsapp', data),
  sendBulk: (data) => api.post('/fee-reminders/whatsapp/bulk', data),
  getHistory: (params) => api.get('/fee-reminders/whatsapp/history', { params }),
};

// Evaluations services
export const evaluationsService = {
  getAll: (params) => api.get('/evaluations', { params }),
  getById: (id) => api.get(`/evaluations/${id}`),
  create: (data) => api.post('/evaluations', data),
  update: (id, data) => api.put(`/evaluations/${id}`, data),
};

// Parents services
export const parentsService = {
  getAll: (params) => api.get('/parents', { params }),
  getById: (id) => api.get(`/parents/${id}`),
  create: (data) => api.post('/parents', data),
  update: (id, data) => api.put(`/parents/${id}`, data),
  delete: (id) => api.delete(`/parents/${id}`),
  linkEleve: (parentId, eleveId) => api.post(`/parents/${parentId}/link-eleve`, { eleve_id: eleveId }),
  unlinkEleve: (parentId, eleveId) => api.delete(`/parents/${parentId}/unlink-eleve/${eleveId}`),
};

// Reference data services
export const referenceService = {
  getProfessions: () => api.get('/professions'),
  getReligions: () => api.get('/religions'),
};

export default api;